"use client";

import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  Send,
} from "lucide-react";
import { useAuth } from "@/lib/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import {
  getSupportCategoriesFor,
  type SupportAudience,
} from "@/lib/config/customerSupport";

interface ContactFormData {
  name: string;
  email: string;
  category: string;
  subject: string;
  message: string;
}

type InlineContactFormProps = {
  audience?: SupportAudience;
  onSubmitted?: () => void;
  className?: string;
};

/** 마이페이지 등에 임베드하는 1:1 문의 폼 */
export function InlineContactForm({
  audience = "customer",
  onSubmitted,
  className = "",
}: InlineContactFormProps) {
  const { user } = useAuth();
  const categories = useMemo(
    () => getSupportCategoriesFor(audience),
    [audience]
  );

  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    category: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      name:
        prev.name ||
        String(
          user?.nickname ||
            (user as { displayName?: string } | null)?.displayName ||
            user?.email?.split("@")[0] ||
            ""
        ),
      email: prev.email || user?.email || "",
    }));
  }, [user]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          userId: user?.uid,
          audience,
        }),
      });

      if (!response.ok) throw new Error("문의 전송에 실패했습니다.");

      setSubmitStatus("success");
      setFormData({
        name: String(
          user?.nickname ||
            (user as { displayName?: string } | null)?.displayName ||
            user?.email?.split("@")[0] ||
            ""
        ),
        email: user?.email || "",
        category: "",
        subject: "",
        message: "",
      });
      onSubmitted?.();
    } catch (error) {
      console.error("문의 전송 오류:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === "success") {
    return (
      <div className={`rounded-2xl border border-gray-200 bg-white px-5 py-10 text-center ${className}`}>
        <CheckCircle className="mx-auto mb-3 h-10 w-10 text-emerald-600" />
        <h2 className="mb-1 text-base font-semibold text-gray-900">
          문의가 접수되었습니다
        </h2>
        <p className="mb-5 text-sm text-gray-500">
          운영팀이 확인 후 답변드립니다.
        </p>
        <Button variant="outline" size="sm" onClick={() => setSubmitStatus("idle")}>
          새 문의 작성
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-4 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 ${className}`}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-600">
            이름 *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-400 focus:outline-none"
            placeholder="홍길동"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-600">
            이메일 *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-400 focus:outline-none"
            placeholder="example@email.com"
          />
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-medium text-gray-600">
          문의 유형 *
        </label>
        <select
          name="category"
          value={formData.category}
          onChange={handleChange}
          required
          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900 focus:border-gray-400 focus:outline-none"
        >
          <option value="">선택해주세요</option>
          {categories.map(cat => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-medium text-gray-600">
          제목 *
        </label>
        <input
          type="text"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          required
          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-400 focus:outline-none"
          placeholder="문의 제목을 입력해주세요"
        />
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-medium text-gray-600">
          문의 내용 *
        </label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={6}
          className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-400 focus:outline-none"
          placeholder="문의 내용을 자세히 작성해주세요."
        />
      </div>

      {submitStatus === "error" && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
          <AlertCircle className="h-4 w-4 shrink-0 text-red-600" />
          <p className="text-sm text-red-700">
            문의 전송에 실패했습니다. 잠시 후 다시 시도해주세요.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between pt-1">
        <p className="text-[11px] text-gray-400">* 필수 입력</p>
        <Button type="submit" disabled={isSubmitting} className="min-w-[110px]">
          {isSubmitting ? (
            "전송 중..."
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <Send className="h-4 w-4" />
              문의하기
            </span>
          )}
        </Button>
      </div>
    </form>
  );
}
